import { useEffect, useRef, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { invalidateCache } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// ISO 3166-1 alpha-2 -> [country name, E.164 calling code (no +)].
// Flag emoji is derived from the ISO2 code itself (regional indicator
// symbols), so it never has to be typed/maintained by hand.
const COUNTRY_DIAL_DATA = {
  US: ["United States", "1"], CA: ["Canada", "1"], MX: ["Mexico", "52"],
  GT: ["Guatemala", "502"], BZ: ["Belize", "501"], SV: ["El Salvador", "503"],
  HN: ["Honduras", "504"], NI: ["Nicaragua", "505"], CR: ["Costa Rica", "506"],
  PA: ["Panama", "507"], CU: ["Cuba", "53"], BS: ["Bahamas", "1242"],
  BB: ["Barbados", "1246"], JM: ["Jamaica", "1876"], HT: ["Haiti", "509"],
  DO: ["Dominican Republic", "1809"], TT: ["Trinidad and Tobago", "1868"],
  GD: ["Grenada", "1473"], LC: ["Saint Lucia", "1758"],
  VC: ["Saint Vincent and the Grenadines", "1784"], AG: ["Antigua and Barbuda", "1268"],
  DM: ["Dominica", "1767"], KN: ["Saint Kitts and Nevis", "1869"],

  BR: ["Brazil", "55"], AR: ["Argentina", "54"], CL: ["Chile", "56"],
  CO: ["Colombia", "57"], PE: ["Peru", "51"], VE: ["Venezuela", "58"],
  EC: ["Ecuador", "593"], BO: ["Bolivia", "591"], PY: ["Paraguay", "595"],
  UY: ["Uruguay", "598"], GY: ["Guyana", "592"], SR: ["Suriname", "597"],

  GB: ["United Kingdom", "44"], IE: ["Ireland", "353"], FR: ["France", "33"],
  DE: ["Germany", "49"], ES: ["Spain", "34"], PT: ["Portugal", "351"],
  IT: ["Italy", "39"], NL: ["Netherlands", "31"], BE: ["Belgium", "32"],
  LU: ["Luxembourg", "352"], CH: ["Switzerland", "41"], AT: ["Austria", "43"],
  SE: ["Sweden", "46"], NO: ["Norway", "47"], DK: ["Denmark", "45"],
  FI: ["Finland", "358"], IS: ["Iceland", "354"], PL: ["Poland", "48"],
  CZ: ["Czech Republic", "420"], SK: ["Slovakia", "421"], HU: ["Hungary", "36"],
  RO: ["Romania", "40"], BG: ["Bulgaria", "359"], GR: ["Greece", "30"],
  HR: ["Croatia", "385"], SI: ["Slovenia", "386"], RS: ["Serbia", "381"],
  BA: ["Bosnia and Herzegovina", "387"], ME: ["Montenegro", "382"],
  MK: ["North Macedonia", "389"], AL: ["Albania", "355"], EE: ["Estonia", "372"],
  LV: ["Latvia", "371"], LT: ["Lithuania", "370"], UA: ["Ukraine", "380"],
  BY: ["Belarus", "375"], MD: ["Moldova", "373"], RU: ["Russia", "7"],
  MT: ["Malta", "356"], CY: ["Cyprus", "357"], LI: ["Liechtenstein", "423"],
  MC: ["Monaco", "377"], AD: ["Andorra", "376"], SM: ["San Marino", "378"],
  VA: ["Vatican City", "379"],

  IN: ["India", "91"], CN: ["China", "86"], JP: ["Japan", "81"],
  KR: ["South Korea", "82"], KP: ["North Korea", "850"], ID: ["Indonesia", "62"],
  MY: ["Malaysia", "60"], SG: ["Singapore", "65"], TH: ["Thailand", "66"],
  VN: ["Vietnam", "84"], PH: ["Philippines", "63"], PK: ["Pakistan", "92"],
  BD: ["Bangladesh", "880"], LK: ["Sri Lanka", "94"], NP: ["Nepal", "977"],
  MM: ["Myanmar", "95"], KH: ["Cambodia", "855"], LA: ["Laos", "856"],
  MN: ["Mongolia", "976"], TW: ["Taiwan", "886"], HK: ["Hong Kong", "852"],
  MO: ["Macau", "853"], AF: ["Afghanistan", "93"], IR: ["Iran", "98"],
  IQ: ["Iraq", "964"], IL: ["Israel", "972"], PS: ["Palestine", "970"],
  JO: ["Jordan", "962"], LB: ["Lebanon", "961"], SY: ["Syria", "963"],
  SA: ["Saudi Arabia", "966"], AE: ["United Arab Emirates", "971"],
  QA: ["Qatar", "974"], KW: ["Kuwait", "965"], BH: ["Bahrain", "973"],
  OM: ["Oman", "968"], YE: ["Yemen", "967"], TR: ["Turkey", "90"],
  GE: ["Georgia", "995"], AM: ["Armenia", "374"], AZ: ["Azerbaijan", "994"],
  KZ: ["Kazakhstan", "7"], UZ: ["Uzbekistan", "998"], TM: ["Turkmenistan", "993"],
  TJ: ["Tajikistan", "992"], KG: ["Kyrgyzstan", "996"], BT: ["Bhutan", "975"],
  MV: ["Maldives", "960"], BN: ["Brunei", "673"], TL: ["Timor-Leste", "670"],

  AU: ["Australia", "61"], NZ: ["New Zealand", "64"], FJ: ["Fiji", "679"],
  PG: ["Papua New Guinea", "675"], SB: ["Solomon Islands", "677"],
  VU: ["Vanuatu", "678"], NC: ["New Caledonia", "687"], PF: ["French Polynesia", "689"],
  WS: ["Samoa", "685"], TO: ["Tonga", "676"], KI: ["Kiribati", "686"],
  TV: ["Tuvalu", "688"], NR: ["Nauru", "674"], FM: ["Micronesia", "691"],
  MH: ["Marshall Islands", "692"], PW: ["Palau", "680"], CK: ["Cook Islands", "682"],

  ZA: ["South Africa", "27"], NG: ["Nigeria", "234"], EG: ["Egypt", "20"],
  KE: ["Kenya", "254"], GH: ["Ghana", "233"], ET: ["Ethiopia", "251"],
  TZ: ["Tanzania", "255"], UG: ["Uganda", "256"], DZ: ["Algeria", "213"],
  MA: ["Morocco", "212"], TN: ["Tunisia", "216"], LY: ["Libya", "218"],
  SD: ["Sudan", "249"], SS: ["South Sudan", "211"], CM: ["Cameroon", "237"],
  CI: ["Ivory Coast", "225"], SN: ["Senegal", "221"], ML: ["Mali", "223"],
  BF: ["Burkina Faso", "226"], NE: ["Niger", "227"], TD: ["Chad", "235"],
  RW: ["Rwanda", "250"], ZM: ["Zambia", "260"], ZW: ["Zimbabwe", "263"],
  MZ: ["Mozambique", "258"], AO: ["Angola", "244"], NA: ["Namibia", "264"],
  BW: ["Botswana", "267"], MW: ["Malawi", "265"], MG: ["Madagascar", "261"],
  MU: ["Mauritius", "230"], SC: ["Seychelles", "248"], DJ: ["Djibouti", "253"],
  SO: ["Somalia", "252"], ER: ["Eritrea", "291"], LR: ["Liberia", "231"],
  SL: ["Sierra Leone", "232"], GN: ["Guinea", "224"], GM: ["Gambia", "220"],
  GW: ["Guinea-Bissau", "245"], MR: ["Mauritania", "222"], CV: ["Cape Verde", "238"],
  TG: ["Togo", "228"], BJ: ["Benin", "229"], GA: ["Gabon", "241"],
  CG: ["Congo", "242"], CD: ["DR Congo", "243"], CF: ["Central African Republic", "236"],
  BI: ["Burundi", "257"], LS: ["Lesotho", "266"], SZ: ["Eswatini", "268"],
  KM: ["Comoros", "269"], ST: ["Sao Tome and Principe", "239"],
  GQ: ["Equatorial Guinea", "240"],
};

// A few common alternate names the bot or a merchant might type that don't
// match the canonical name above (e.g. "USA", "UK") -> ISO2.
const NAME_ALIASES = {
  "USA": "US", "UNITED STATES OF AMERICA": "US", "U.S.A": "US", "U.S.": "US",
  "UK": "GB", "GREAT BRITAIN": "GB", "ENGLAND": "GB", "SCOTLAND": "GB", "WALES": "GB", "NORTHERN IRELAND": "GB",
  "SOUTH KOREA": "KR", "KOREA": "KR", "REPUBLIC OF KOREA": "KR",
  "UAE": "AE", "EMIRATES": "AE",
  "RUSSIA": "RU", "RUSSIAN FEDERATION": "RU",
  "VIETNAM": "VN", "VIET NAM": "VN",
  "CZECHIA": "CZ",
  "IVORY COAST": "CI", "COTE D'IVOIRE": "CI",
  "DRC": "CD", "CONGO DR": "CD", "DEMOCRATIC REPUBLIC OF THE CONGO": "CD",
  "BRASIL": "BR", "BHARAT": "IN",
};

function flagEmoji(iso2) {
  return iso2.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

// Every country, sorted by name, for the dropdown. Multiple countries can
// (and do) share the same calling code -- e.g. US/Canada both show "+1" --
// that's expected and matches how every phone-input picker handles it.
const COUNTRY_CODES = Object.entries(COUNTRY_DIAL_DATA)
  .map(([iso2, [name, dial]]) => ({ iso2, code: `+${dial}`, label: `${flagEmoji(iso2)} ${name} (+${dial})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

// address.country -> ISO2, e.g. "Sweden" / "SE" / "SWEDEN" -> "SE".
// Returns null (leave whatever's currently selected untouched) if nothing matches.
function isoForCountry(input) {
  if (!input) return null;
  const v = String(input).trim().toUpperCase();
  if (COUNTRY_DIAL_DATA[v]) return v;
  if (NAME_ALIASES[v] && COUNTRY_DIAL_DATA[NAME_ALIASES[v]]) return NAME_ALIASES[v];
  const byName = Object.entries(COUNTRY_DIAL_DATA).find(([, [name]]) => name.toUpperCase() === v);
  return byName ? byName[0] : null;
}

function dialCodeFromIso(iso2) {
  const entry = COUNTRY_DIAL_DATA[iso2];
  return entry ? `+${entry[1]}` : "+91";
}

// Several ISO2s share one calling code (US/CA both "1", RU/KZ both "7"). When all
// we have is a raw phone number with no address country to disambiguate, pick one
// specific, predictable ISO2 per shared code instead of letting the <select> land
// on whichever option with that value happens to come first alphabetically.
const DEFAULT_ISO_FOR_DIAL_CODE = { "+1": "US", "+7": "RU" };

function splitPhone(phone) {
  if (!phone) return { iso2: "IN", local: "" };
  const byLength = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
  const found = byLength.find((c) => phone.startsWith(c.code));
  if (!found) return { iso2: "IN", local: phone.replace(/^\+/, "") };
  const iso2 = DEFAULT_ISO_FOR_DIAL_CODE[found.code] || found.iso2;
  return { iso2, local: phone.slice(found.code.length) };
}

// Searchable country-code combobox: a text input that filters the ~200-entry
// COUNTRY_CODES list live, instead of one long native <select> a merchant has
// to scroll or blind-type through. Matches on country name, ISO2, or dial code.
function CountryCodeCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  const selected = COUNTRY_CODES.find((c) => c.iso2 === value);

  const filtered = query.trim()
    ? COUNTRY_CODES.filter((c) => {
        const q = query.trim().toLowerCase();
        return (
          c.label.toLowerCase().includes(q) ||
          c.iso2.toLowerCase() === q ||
          c.code.replace("+", "").startsWith(q.replace("+", ""))
        );
      })
    : COUNTRY_CODES;

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={wrapRef} className="relative max-w-[45%] flex-shrink-0">
      <input
        value={open ? query : selected?.label || ""}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          setQuery("");
        }}
        placeholder="Search country..."
        data-testid="edit-country-code"
        className="w-full rounded-2xl border border-ink/15 px-3 py-2 focus:border-hotpink focus:outline-none"
      />
      {open && (
        <ul
          role="listbox"
          className="absolute left-0 top-full z-30 mt-1 max-h-64 w-64 overflow-y-auto rounded-2xl border border-ink/15 bg-white py-1 shadow-lg"
        >
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-sm text-ink/40">No matches</li>
          )}
          {filtered.map((c) => (
            <li
              key={c.iso2}
              role="option"
              aria-selected={c.iso2 === value}
              onMouseDown={(e) => {
                // onMouseDown (not onClick) so this fires before the
                // click-outside handler closes the list on blur.
                e.preventDefault();
                onChange(c.iso2);
                setOpen(false);
                setQuery("");
              }}
              className={`cursor-pointer px-3 py-2 text-sm hover:bg-ink/5 ${
                c.iso2 === value ? "bg-hotpink/10 font-semibold" : ""
              }`}
            >
              {c.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function CustomerEditDialog({ open, onOpenChange, customer, onSaved }) {
  const [name, setName] = useState("");
  const [countryIso2, setCountryIso2] = useState("IN");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [email, setEmail] = useState("");
  const [igHandle, setIgHandle] = useState("");
  const [addr, setAddr] = useState({ address_line: "", city: "", state: "", pincode: "", country: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!customer) return;
    setName(customer.customer_name || "");
    const { iso2, local } = splitPhone(customer.customer_phone || "");
    setCountryIso2(iso2);
    setPhoneLocal(local);
    setEmail(customer.customer_email || "");
    setIgHandle(customer.customer_ig_handle || "");

    const a = customer.address && typeof customer.address === "object" ? customer.address : {};
    setAddr({
      address_line: a.address_line || "",
      city: a.city || "",
      state: a.state || "",
      pincode: a.pincode || "",
      country: a.country || "",
    });
  }, [customer]);

  // Keep the phone country code in sync with the delivery-address country --
  // on load, and live as the merchant edits the Country field below. Only
  // overrides when the country text actually resolves to a known code; an
  // unrecognized/partial entry leaves whatever's currently selected alone.
  useEffect(() => {
    const detected = isoForCountry(addr.country);
    if (detected) setCountryIso2(detected);
  }, [addr.country]);

  const handleSave = async () => {
    if (!customer) return;
    setSaving(true);
    try {
      await api.patch(`/merchant/customers/update`, {
        customer_ig_id: customer.customer_ig_id,
        customer_name: name.trim(),
        country_code: dialCodeFromIso(countryIso2),
        phone_local: phoneLocal.trim(),
        customer_email: email.trim(),
        customer_ig_handle: igHandle.trim().replace(/^@/, ""),
        address: addr,
      });
      toast.success("Customer updated.");
      invalidateCache("/merchant/customers");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      // PATCH errors come back as { error: "..." }, not { detail: "..." }
      toast.error(e?.response?.data?.error || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-2xl overflow-y-auto bg-white"
        data-testid="customer-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Edit customer
          </DialogTitle>
          <DialogDescription>
            Update the contact details and delivery address. Changes apply to future
            orders for this Instagram customer.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="edit-name"
              className="w-full rounded-2xl border border-ink/15 px-3 py-2 focus:border-hotpink focus:outline-none"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="edit-email"
              className="w-full rounded-2xl border border-ink/15 px-3 py-2 focus:border-hotpink focus:outline-none"
            />
          </label>
          <div className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">Phone</span>
            <div className="flex gap-2">
              <CountryCodeCombobox value={countryIso2} onChange={setCountryIso2} />
              <input
                value={phoneLocal}
                onChange={(e) => setPhoneLocal(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="9812345678"
                data-testid="edit-phone-local"
                className="flex-1 rounded-2xl border border-ink/15 px-3 py-2 focus:border-hotpink focus:outline-none"
              />
            </div>
            <p className="mt-1 text-xs text-ink/50">
              Auto-set from the delivery address country below — change it manually if this customer's
              phone number is from a different country.
            </p>
          </div>
          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-bold uppercase tracking-[0.15em] text-ink/60">Instagram handle</span>
            <div className="flex items-center gap-2 rounded-2xl border border-ink/15 px-3 py-2 focus-within:border-hotpink">
              <span className="text-ink/40">@</span>
              <input
                value={igHandle}
                onChange={(e) => setIgHandle(e.target.value.replace(/^@/, ""))}
                placeholder="username"
                data-testid="edit-ig-handle"
                className="w-full bg-transparent focus:outline-none"
              />
            </div>
            <p className="mt-1 text-xs text-ink/50">
              If the bot already collected this after payment, it won't ask again. Add or fix it
              here if the customer skipped it or typed it wrong.
            </p>
          </label>
        </div>

        <div className="mt-2 border-t border-ink/10 pt-4">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-hotpink">
            Delivery address
          </p>
          <p className="mt-1 text-xs text-ink/50">
            If this was collected before structured address fields existed, it may all be sitting in
            Address line — feel free to split it into the right fields below.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <input value={addr.address_line} onChange={(e) => setAddr({ ...addr, address_line: e.target.value })}
              placeholder="Address line (house/flat no., street, area)" data-testid="edit-addr-line1"
              className="col-span-2 rounded-2xl border border-ink/15 px-3 py-2 text-sm focus:border-hotpink focus:outline-none" />
            <input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })}
              placeholder="City" data-testid="edit-addr-city"
              className="rounded-2xl border border-ink/15 px-3 py-2 text-sm focus:border-hotpink focus:outline-none" />
            <input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })}
              placeholder="State" data-testid="edit-addr-state"
              className="rounded-2xl border border-ink/15 px-3 py-2 text-sm focus:border-hotpink focus:outline-none" />
            <input value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })}
              placeholder="Pincode" data-testid="edit-addr-pincode"
              className="rounded-2xl border border-ink/15 px-3 py-2 text-sm focus:border-hotpink focus:outline-none" />
            <input value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })}
              placeholder="Country" data-testid="edit-addr-country"
              className="rounded-2xl border border-ink/15 px-3 py-2 text-sm focus:border-hotpink focus:outline-none" />
          </div>
        </div>

        <div className="mt-2 flex gap-3">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="edit-cancel"
            className="flex-1 rounded-full border border-ink/15 bg-white px-5 py-2 text-sm font-semibold text-ink hover:bg-ink/5"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="edit-save"
            className="cta-glow inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-sunset-gradient px-6 py-2 text-sm font-semibold text-white disabled:opacity-70"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save changes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
